/**
 * Phase 20 wizard — Step 3: finish / material level.
 *
 * Reuses the existing material `OptionsCardGrid`. On entry the step asks
 * Stella ONCE (never in a loop) to raise the options and renders them as a
 * tappable grid right here on the guided step when they arrive. Material
 * options are per-category and conversational, so if Stella needs more
 * context first (a kitchen may need "which surface?"), the "Choose with
 * Stella" affordance lets the customer pick conversationally. Either path
 * records a selection, which completes the step (via the settle-refetch).
 */

import { useEffect, useRef } from "react";

import type { OptionsCardPayload } from "@starz/shared/api/types.gen";
import { OptionsCardGrid } from "@/components/chat/OptionsCardGrid";

interface Props {
  /** Latest material options payload from the conversation, if any. */
  payload: OptionsCardPayload | null;
  selectedOptionId: string | null;
  onSelect: (card: OptionsCardPayload["cards"][number]) => void;
  /** Ask Stella to raise material options (called at most once). */
  onRequestOptions: () => void;
  /** Open the chat to choose finishes conversationally. */
  onChooseWithStella: () => void;
  /** A Stella turn is streaming (e.g. options are loading). */
  streaming?: boolean;
  disabled?: boolean;
}

export function FinishStep({
  payload,
  selectedOptionId,
  onSelect,
  onRequestOptions,
  onChooseWithStella,
  streaming = false,
  disabled = false,
}: Props) {
  const hasOptions = payload != null && payload.cards.length > 0;

  // Ask Stella for options exactly once on entry — never in a loop, and
  // without leaving the guided step. The grid renders here when they load.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (!requestedRef.current && !hasOptions && !streaming && !disabled) {
      requestedRef.current = true;
      onRequestOptions();
    }
  }, [hasOptions, streaming, disabled, onRequestOptions]);

  return (
    <div className="space-y-4">
      {hasOptions ? (
        <>
          <p className="text-sm text-warm-gray">
            Pick the finish that fits your project. You can change it anytime.
          </p>
          <OptionsCardGrid
            payload={payload}
            selectedOptionId={selectedOptionId}
            disabled={disabled || streaming || selectedOptionId !== null}
            onSelect={onSelect}
          />
        </>
      ) : (
        <div className="rounded-xl border border-starz-gold/20 bg-starz-cream/30 p-6 text-sm text-warm-gray">
          {streaming
            ? "Stella is putting your finish options together…"
            : "Stella will help you choose finishes for your project — tell her which surfaces you're remodeling (cabinets, countertops, flooring, and so on)."}
        </div>
      )}

      <div className="border-t border-starz-gold/15 pt-4">
        <p className="text-sm text-warm-gray">
          Prefer to talk it through, or don't see the right option?
        </p>
        <button
          type="button"
          onClick={onChooseWithStella}
          disabled={disabled}
          className="mt-2 min-h-[44px] rounded-lg border border-starz-gold/40 px-5 py-3 text-sm font-semibold text-starz-navy transition-colors hover:bg-starz-cream disabled:opacity-40"
        >
          💬 Choose your finish with Stella
        </button>
      </div>
    </div>
  );
}
